<script setup lang="ts">
import { computed } from 'vue'
import GbBreadcrumbs, { type BreadcrumbItem } from '@/components/GbBreadcrumbs.vue'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable'
import { gradesToLabels } from '@/util/gradesort'
import { keepingBetaOptIn } from '@/util/beta_opt_in'
import { toolkitUrls } from '@/util/links'

const props = defineProps<{
  subject: string
  subjectDomains: string[]
  grades: string[]
  title: string
}>()

type BrowseGoalsQuery = {
  subject?: string
  subjectDomain?: string
  grades?: string[]
}

const browseGoalsUrl = (query: BrowseGoalsQuery = {}) => {
  const params = new URLSearchParams()
  if (query.subject) params.append('subject', query.subject)
  if (query.subjectDomain) params.append('subjectDomain', query.subjectDomain)
  query.grades?.forEach((grade) => params.append('grades', grade))
  const queryString = params.toString()
  return keepingBetaOptIn(queryString ? `${toolkitUrls.browse}?${queryString}` : toolkitUrls.browse)
}

const gradeRangeLabel = (grades: string[]) => {
  const labels = gradesToLabels(grades)
  if (labels.length <= 1) {
    return labels[0]
  }
  return `${labels[0]}-${labels[labels.length - 1]}`
}

const firstSubjectDomain = computed(() => props.subjectDomains[0])

const subjectCrumb = computed((): BreadcrumbItem | null => {
  if (!props.subject) return null
  return { label: getGoalSubjectLabelFromValue(props.subject), href: browseGoalsUrl({ subject: props.subject }) }
})

const subjectDomainCrumb = computed((): BreadcrumbItem | null => {
  if (!firstSubjectDomain.value) return null
  return {
    label: firstSubjectDomain.value,
    href: browseGoalsUrl({ subject: props.subject, subjectDomain: firstSubjectDomain.value })
  }
})

const gradesCrumb = computed((): BreadcrumbItem | null => {
  if (props.grades.length === 0) return null
  return {
    label: `Grade ${gradeRangeLabel(props.grades)}`,
    href: browseGoalsUrl({ subject: props.subject, subjectDomain: firstSubjectDomain.value, grades: props.grades })
  }
})

const items = computed((): BreadcrumbItem[] => {
  const optionalCrumbs = [subjectCrumb.value, subjectDomainCrumb.value, gradesCrumb.value]
  return [
    { label: 'Browse', href: browseGoalsUrl() },
    ...optionalCrumbs.filter((crumb): crumb is BreadcrumbItem => crumb !== null),
    { label: props.title }
  ]
})
</script>

<template>
  <GbBreadcrumbs :items="items" aria-label="Breadcrumb" />
</template>
